/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
import { createSelector } from 'reselect';
import { programmeTypes } from '@constants/index';
import { emptyList, emptyObject } from '@constants/empty';
import { getItem } from './items';
import { getTopologyOrEmptyObject } from './topologies';
import { getUser } from './users';

export const isScheduledSelector =
  (courseUID) =>
  ({ courses }) =>
    courses.data[courseUID]?.plannerSchedule?.is_scheduled || false;

export const plannerScheduleSelector =
  (courseUID) =>
  ({ courses }) =>
    courses.data[courseUID]?.plannerSchedule || emptyObject;

export const isCompletedSelector =
  (courseUID, defaultValue) =>
  ({ courses }) =>
    courses.data[courseUID]?.isCompleted === 1 || defaultValue;

export const isOnlyRecordedClassesPresentSelector =
  (courseUID) =>
  ({ courses }) =>
    (courses.data[courseUID]?.courseType ===
      programmeTypes.HYBRID_NON_OFFLINE &&
      courses.data[courseUID]?.isRecordedClassesOnlyCourse) ??
    false;

const getCourseData = (state, courseUID) => state.courses.data[courseUID];

// eslint-disable-next-line import/prefer-default-export
export const getFullCourseInfo = (state, courseUID) => {
  const courseData = getCourseData(state, courseUID);
  return {
    ...courseData,
    authors: courseData.authors.map((author) => getUser(state, author.user)),
    nextTrack: getItem(state, courseData.nextTrack.item),
    previousTrack: getItem(state, courseData.previousTrack.item),
    liveClasses: (courseData.liveClasses || emptyList).map((liveClass) =>
      getItem(state, liveClass.item)
    ),
    goal: courseData.goal
      ? getTopologyOrEmptyObject(state, courseData.goal.topology)
      : null,
    topicGroups: (courseData.topicGroups || emptyList).map((topicGroup) =>
      getTopologyOrEmptyObject(state, topicGroup.topology)
    ),
    topologies: (courseData.topologies || emptyList).map((topicGroup) =>
      getTopologyOrEmptyObject(state, topicGroup.topology)
    )
  };
};

const getResults = ({ paginator }, query) =>
  paginator.searchResult?.[query]?.results;
const getFeedrows = ({ feedrows }) => feedrows.data;
const getItems = ({ items }) => items.data;
const getCourses = ({ courses }) => courses.data;
const getBatches = ({ batches }) => batches.data;
const getTopologies = ({ topologies }) => topologies.data;
const getUsers = ({ users }) => users.data;
const getCombat = ({ combat }) => combat.data;
const getStoreItems = ({ store }) => store.data;

const getPagSeeAllResults = ({ paginator }, query) =>
  paginator.searchList?.[query];

const getParam = (q, query, query2) => query2;

const coursesTypes = ['special_class', 'programme', 'test_series'];

const getSearchResults = createSelector(
  [
    getResults,
    getFeedrows,
    getItems,
    getCourses,
    getBatches,
    getTopologies,
    getUsers,
    getCombat
  ],
  (results, feedrows, items, courses, batches, topologies, users, combat) => {
    const feeds = [];
    for (let i = 0; i < results?.length; i++) {
      feeds.push(JSON.parse(JSON.stringify(feedrows[results[i].feedrow])));
    }
    let requestID = null;
    feeds.forEach((feed) => {
      if (!requestID) requestID = feed.extraBlockinfo?.requestID;
      let tempItems = [];
      if (feed.type === 'post') {
        feed.courses.forEach((feedCourse) => {
          const feedCourseitemId = feedCourse.item;
          const feedCourseitem = JSON.parse(
            JSON.stringify(items[feedCourseitemId])
          );
          if (feedCourseitem.author) {
            feedCourseitem.author = users[feedCourseitem.author];
          }
          if (feedCourseitem.programme?.course) {
            const prog = courses[feedCourseitem.programme.course];
            feedCourseitem.programme = prog;
          }
          tempItems.push(feedCourseitem);
        });
        feed.courses = tempItems;
        tempItems = [];
      } else if (coursesTypes.includes(feed.type)) {
        feed.courses.forEach((feedCourse) => {
          const feedCourseitemId = feedCourse.course;
          const feedCourseitem = JSON.parse(
            JSON.stringify(courses[feedCourseitemId]) || '{}'
          );
          if (feedCourseitem.authors.length) {
            const newTemp = feedCourseitem.authors.map((author) => {
              return users[author?.user];
            });
            feedCourseitem.authors = newTemp;
          }

          if (feedCourseitem.nextTrack?.item) {
            const nextTrack = items[feedCourseitem.nextTrack.item];
            feedCourseitem.nextTrack = nextTrack;
          }
          tempItems.push(feedCourseitem);
        });
        feed.courses = tempItems;
        tempItems = [];
      } else if (feed.type === 'batch') {
        feed.batches.forEach((feedBatch) => {
          const feedBatchitemId = feedBatch.batch;

          const feedBatchitem = JSON.parse(
            JSON.stringify(batches[feedBatchitemId])
          );
          if (feedBatchitem.goal?.topology) {
            feedBatchitem.goal = topologies[feedBatchitem.goal.topology];
          }
          if (feedBatchitem.authors?.length) {
            const authors = feedBatchitem.authors.map((author) => {
              return users[author.user];
            });
            feedBatchitem.authors = authors;
          }
          tempItems.push(feedBatchitem);
        });
        feed.batches = tempItems;
        tempItems = [];
      } else if (feed.type === 'topology') {
        feed.topologies.forEach((feedTopology) => {
          const feedTopologyitemId = feedTopology.topology;
          const feedTopologyitem = topologies[feedTopologyitemId];
          tempItems.push(feedTopologyitem);
        });
        feed.topologies = tempItems;
        tempItems = [];
      } else if (feed.type === 'user') {
        feed.users.forEach((feedUser) => {
          const feedUseritemId = feedUser.user;
          const feedUseritem = users[feedUseritemId];
          tempItems.push(feedUseritem);
        });
        feed.users = tempItems;
        tempItems = [];
      } else if (feed.type === 'combat') {
        feed.combatData = feed.combatData.map((combatB) => {
          const combatUId = combatB.combat;
          const data = combat[combatUId];
          return {
            ...combatB,
            ...data,
            author: users[data?.user] || ''
          };
        });
      }
    });
    return { feeds, requestID };
  }
);

export const getSearchSeeALLResults = createSelector(
  [
    getPagSeeAllResults,
    getItems,
    getCourses,
    getBatches,
    getTopologies,
    getUsers,
    getParam,
    getCombat,
    getStoreItems
  ],
  (
    feedResults,
    items,
    courses,
    batches,
    topologies,
    users,
    feedType,
    combat,
    store
  ) => {
    // const feeds = [];
    const { results = [] } = feedResults || {};
    let tempItems = [];
    if (feedType === 'post') {
      tempItems = [];
      results.forEach((feedCourse) => {
        const feedCourseitemId = feedCourse.item;
        const feedCourseitem = JSON.parse(
          JSON.stringify(items[feedCourseitemId])
        );
        if (feedCourseitem.author) {
          feedCourseitem.author = users[feedCourseitem.author];
        }
        if (feedCourseitem.programme?.course) {
          const prog = courses[feedCourseitem.programme.course];
          feedCourseitem.programme = prog;
        }
        tempItems.push(feedCourseitem);
      });
      // feed.courses = tempItems;
    } else if (coursesTypes.includes(feedType)) {
      tempItems = [];
      results.forEach((feedCourse) => {
        const feedCourseitemId = feedCourse.course;
        const feedCourseitem = JSON.parse(
          JSON.stringify(courses[feedCourseitemId])
        );
        if (feedCourseitem.authors.length) {
          const newTemp = feedCourseitem.authors.map((author) => {
            return users[author?.user];
          });
          feedCourseitem.authors = newTemp;
        }
        if (feedCourseitem.nextTrack?.item) {
          const nextTrack = items[feedCourseitem.nextTrack.item];
          feedCourseitem.nextTrack = nextTrack;
        }
        tempItems.push(feedCourseitem);
      });
      // feed.courses = tempItems;
    } else if (feedType === 'batch') {
      tempItems = [];
      results.forEach((feedBatch) => {
        const feedBatchitemId = feedBatch.batch;
        const feedBatchitem = JSON.parse(
          JSON.stringify(batches[feedBatchitemId])
        );
        if (feedBatchitem.goal?.topology) {
          feedBatchitem.goal = topologies[feedBatchitem.goal.topology];
        }
        if (feedBatchitem.authors?.length) {
          const authors = feedBatchitem.authors.map((author) => {
            return users[author.user];
          });
          feedBatchitem.authors = authors;
        }
        tempItems.push(feedBatchitem);
      });
      // feed.batches = tempItems;
    } else if (feedType === 'topology') {
      tempItems = [];
      results.forEach((feedTopology) => {
        const feedTopologyitemId = feedTopology.topology;
        const feedTopologyitem = topologies[feedTopologyitemId];
        tempItems.push(feedTopologyitem);
      });
      // feed.topologies = tempItems;
    } else if (feedType === 'user') {
      tempItems = [];
      results.forEach((feedUser) => {
        const feedUseritemId = feedUser.user;
        const feedUseritem = users[feedUseritemId];
        tempItems.push(feedUseritem);
      });
      // results.users = tempItems;
    } else if (feedType === 'combat') {
      tempItems = [];
      results.forEach((combatB) => {
        const combatUID = combatB.combat;
        const combatData = combat[combatUID];
        combatData.author = users[combatData.user];
        tempItems.push(combatData);
      });
      // results.users = tempItems;
    } else if (feedType === 'store') {
      tempItems = [];
      results.forEach((data) => {
        const storeItemId = data.store;
        const storeItem = store[storeItemId];
        tempItems.push({ ...storeItem });
      });
    }
    const itemData = {
      ...feedResults,
      results: tempItems
    };
    return itemData;
  }
);

export const isEnrolledOfACourse =
  (courseUID) =>
  ({ courses }) => {
    return courses.data[courseUID]?.isEnrolled;
  };

export const getTotalPracticeDPPCount =
  (courseUID) =>
  ({ courses }) =>
    courses.data[courseUID]?.totalPracticeDPPCount || 0;

export const getTotalPracticeDPPQuestionCount =
  (courseUID) =>
  ({ courses }) =>
    courses.data[courseUID]?.totalPracticeQuestionCount || 0;

export default getSearchResults;
